import {
  ArrowRight,
  Ban,
  Building2,
  CalendarClock,
  Check,
  ChevronRight,
  Factory,
  PackageX,
  TrainFront,
  User,
  Zap,
} from 'lucide-react';
import type { ReactNode } from 'react';
import {
  type RequisitionDto,
} from '@eombor/contracts';
import { Badge, Button, Card, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import {
  REQUISITION_MAIN_PATH,
  daysUntilDue,
  deadlineStateOf,
  isPickupStale,
  isStoppedStatus,
  mainPathIndex,
  requisitionStatusTone,
  waitingDays,
} from '../model/requisition-status.js';
import {
  requisitionPendingText,
  requisitionStatusText,
} from '../../../shared/i18n/labels.js';
import { requesterOriginOf } from '../model/requester-origin.js';

/**
 * TALABNOMA KARTOCHKASI — ro'yxatdagi bitta hujjat.
 *
 * Jadval o'rniga kartochka, chunki ro'yxatning savoli "ustunlarni
 * solishtirish" emas: odam bitta narsani bilmoqchi — MENING talabnomam
 * qayerda va yana necha qadam qoldi. Jadvalda bu savolga faqat holat
 * ustunidagi so'z javob berardi va "Buxgalter tasdiqladi" ning oxirgi
 * bosqichmi yoki o'rtasimi ekani ko'rinmasdi.
 *
 * Shuning uchun markazda YO'L CHIZIG'I turadi: nechta bosqich bosib
 * o'tilgani va nechtasi qolgani bir qarashda ko'rinadi.
 */

/** Sana: "12.08.2026". Muddat kun aniqligida — vaqt ko'rsatilmaydi. */
const formatDate = (iso: string | null): string | null => {
  if (iso === null) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/**
 * KELIB CHIQISH bedji — stansiya yoki korxona.
 *
 * Rang semantik tokendan olinadi (HEX faqat `tokens.css` da yashaydi):
 * stansiya — `success` (tizimdagi eng yaqin feruza), korxona — `info`
 * (ko'k). Qorong'i temada tokenlar o'zi almashadi.
 */
const ORIGIN_TONES = {
  station: {
    pill: 'border-success/50 bg-success-soft text-success',
    dot: 'bg-success text-success-contrast',
  },
  enterprise: {
    pill: 'border-info/50 bg-info-soft text-info',
    dot: 'bg-info text-white',
  },
} as const;

const OriginPill = ({
  tone,
  icon,
  value,
}: {
  tone: keyof typeof ORIGIN_TONES;
  icon: ReactNode;
  value: string;
}) => (
  <span
    title={value}
    className={cn(
      'inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-[var(--radius-pill)]',
      'border-[0.5px] py-0.5 pr-2.5 pl-0.5 text-xs leading-none font-medium',
      ORIGIN_TONES[tone].pill,
    )}
  >
    <span
      aria-hidden
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-full',
        ORIGIN_TONES[tone].dot,
      )}
    >
      {icon}
    </span>
    {/* `py-px` — `truncate` ning `overflow: hidden` i `leading-none` da
        harf dumlarini (y, g) kesib qo'ymasin. */}
    <span className="min-w-0 truncate py-px">{value}</span>
  </span>
);

export interface RequisitionListCardProps {
  requisition: RequisitionDto;
  /** Ombor nomi identifikator o'rniga — nomlar boshqa so'rovdan keladi. */
  warehouseName?: string;
  /**
   * Talabnomani YOZGAN korxona — qisqa belgi bilan ("PCH-5").
   *
   * Ombor bittada qolmaydi: MTU markaziy omboriga butun MTU dagi
   * korxonalar yozadi va ro'yxatda o'nta kartochkaning hammasida bir xil
   * ombor nomi turadi. Ularni ajratadigan yagona narsa — qaysi korxona
   * so'ragani.
   *
   * Ish joyi biriktirilmagan xodim yozgan hujjatda korxona YO'Q
   * (`requesterOrgUnitId` — `null`), shuning uchun qator umuman
   * chizilmaydi: bo'sh "—" nima demoqchi ekani noaniq bo'lardi.
   */
  requesterOrgUnitName?: string;
  /**
   * BUYURTMACHINING ism-familiyasi — sahifa uni hujjatdan topadi
   * (`useRequesterNames`). Server ismni ro'yxatda o'zi bersa, o'sha ustun.
   */
  requesterName?: string;
  onOpen?: () => void;
}

export const RequisitionListCard = ({
  requisition,
  warehouseName,
  requesterOrgUnitName,
  requesterName,
  onOpen,
}: RequisitionListCardProps) => {
  /* TO'XTAGAN hujjat — rad etilgani ham, bekor qilingani ham. Ikkovida
     ham yo'l shu yerda uzilgan, shuning uchun bosib o'tilgan qismi QIZIL
     bo'lib qoladi: yashil "hammasi joyida" degan ma'noni berardi.
     Ilgari faqat sabab YOZILGAN hujjat qizil bo'lardi va sababsiz bekor
     qilingani yashil bo'lib turaverardi. */
  const stopped = isStoppedStatus(requisition.status);

  /* MUDDAT — kun aniqligida. `today` alohida holat va aynan shu narsa
     yetishmasdi: bugungi sana qizil «Muddati o'tgan» bo'lib chiqardi,
     holbuki u o'tmagan — u TEZKOR. */
  const deadline = deadlineStateOf(requisition.status, requisition.neededBy);
  const daysLeft = daysUntilDue(requisition.neededBy);
  const overdue = deadline === 'overdue';
  const urgent = deadline === 'today';

  /* Yo'ldagi o'rin. To'xtagan hujjat asosiy yo'lda umuman turmaydi
     (`-1`) va uning qayerda uzilgani ro'yxat javobidan bilinmaydi —
     shuning uchun bunday hujjatda chiziq o'rin ko'rsatmaydi, faqat
     to'xtaganini aytadi. */
  /* UNUTILGAN BANDLIK — tayyor, lekin olib ketilmagan. Bunday hujjat
     qoldiqni ushlab turadi va uni hech kim payqamasa, mahsulot boshqa
     talabnomaga hech qachon yetib bormaydi. */
  const stale = isPickupStale(requisition.status, requisition.readyAt);
  const waiting = waitingDays(requisition.readyAt);

  const index = mainPathIndex(requisition.status);
  const total = REQUISITION_MAIN_PATH.length;
  const passed = index < 0 ? 0 : index + 1;
  const needed = formatDate(requisition.neededBy);

  /* KEYINGI qadam — ayni damda kutilayotgani. Oxirgi bosqichda (`CLOSED`)
     kutiladigan narsa qolmaydi. */
  const awaiting = stopped ? undefined : REQUISITION_MAIN_PATH[passed];

  /* STANSIYA va KORXONA — server qaytarsa. Korxona nomi kelmasa,
     sahifa `requesterOrgUnitId` dan topgan qisqa belgi ishlatiladi. */
  const origin = requesterOriginOf(requisition);
  const station = origin.station;
  const enterprise = origin.enterprise ?? requesterOrgUnitName ?? null;
  const creator = origin.creator ?? requesterName ?? null;

  return (
    <Card
      className={cn(
        'h-full border-l-4 transition-colors',
        stopped || overdue
          ? 'border-l-danger'
          : urgent
            ? 'border-l-warn'
            : 'border-l-[var(--color-accent)]',
      )}
      /* Bolalar `Card` ning ICHKI qutisida turadi — ustun va oraliq shu
         yerda beriladi, `className` (tashqi `section`) ga emas. */
      bodyClassName="flex flex-col gap-2.5"
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="tabular text-sm font-semibold whitespace-nowrap text-text">
            {requisition.docNo}
          </span>
          <Badge tone={requisitionStatusTone(requisition.status)}>
            {requisitionStatusText(requisition.status)}
          </Badge>
          {/* Muddati o'tgani — kartochkadagi YAGONA qizil belgi, shuning
              uchun u ko'zga birinchi tashlanadi. Bugun so'ralgani esa
              SARIQ: u kechikmagan, lekin bugun ulgurish kerak. */}
          {overdue ? <Badge tone="danger">{t('requisition.overdue')}</Badge> : null}
          {urgent ? (
            <Badge tone="warn">
              <Zap size={11} aria-hidden />
              {t('requisition.urgent')}
            </Badge>
          ) : null}
          {stale && waiting !== null ? (
            <Badge tone="warn">
              <PackageX size={11} aria-hidden />
              {t('requisition.pickupStale', { count: waiting })}
            </Badge>
          ) : null}
        </div>

        {onOpen === undefined ? null : (
          <Button variant="secondary" onClick={onOpen} className="shrink-0">
            {t('requisition.open')}
            <ArrowRight size={14} aria-hidden />
          </Button>
        )}
      </header>

      {/* Ikonka TEPAGA yopishgan: uzun nom ikki qatorga o'tsa ham u siljimaydi. */}
      <div className="flex min-w-0 items-start gap-1.5 text-xs leading-[1.4] text-text-muted">
        <Building2 size={14} aria-hidden className="shrink-0" />
        <span className="min-w-0 [overflow-wrap:anywhere]">
          {warehouseName ?? `#${requisition.warehouseId}`}
        </span>
      </div>

      <p className="line-clamp-2 text-sm text-pretty [overflow-wrap:anywhere] text-text">
        {requisition.purpose}
      </p>

      {/* Ikkalasi ham bo'lmasa qator (konteyner ham) chizilmaydi. */}
      {station === null && enterprise === null ? null : (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {station === null ? null : (
            <OriginPill
              tone="station"
              icon={<TrainFront size={11} />}
              value={station}
            />
          )}
          {enterprise === null ? null : (
            <OriginPill
              tone="enterprise"
              icon={<Factory size={11} />}
              value={enterprise}
            />
          )}
        </div>
      )}

      {/*
        NIMA BO'LDI va NIMA KUTILMOQDA — kartochkaning asosiy javobi.
        Yolg'iz holat belgisi «Korxona tasdiqladi» deb aytardi, lekin
        odamning savoli bundan keyingisi: "endi kim qiladi?". Ikki qator
        shu savolga javob beradi: tepada bo'lib o'tgani xira va kichik,
        pastda kutilayotgani sariq va yirik.
      */}
      <div
        className={cn(
          'flex flex-col gap-2 rounded-[var(--radius-md)] border px-3.5 py-3',
          stopped ? 'border-danger/25 bg-danger-soft' : 'border-border bg-surface-2',
        )}
      >
        {stopped ? (
          <span className="flex items-center gap-2 text-sm font-semibold text-danger">
            <Ban size={15} aria-hidden className="shrink-0" />
            {requisitionStatusText(requisition.status)}
          </span>
        ) : (
          <>
            <span className="flex items-center gap-2 text-xs leading-5 text-text-muted">
              <Check size={13} aria-hidden className="shrink-0 text-success" />
              <span className="min-w-0 truncate">
                {requisitionStatusText(requisition.status)}
              </span>
            </span>
            {awaiting === undefined ? null : (
              <span className="flex items-center gap-2 text-sm leading-6 font-semibold text-warn">
                <ChevronRight size={15} aria-hidden className="shrink-0 opacity-70" />
                <span
                  aria-hidden
                  className={cn(
                    'size-2 shrink-0 rounded-[var(--radius-pill)] bg-warn',
                    'motion-pulse-soft [--pulse-color:var(--color-warn)]',
                  )}
                />
                <span className="min-w-0 text-pretty">{requisitionPendingText(awaiting)}</span>
              </span>
            )}
          </>
        )}
      </div>

      {/* YO'L — nechta bosqich o'tilgani va nechtasi qolgani. */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between text-xs">
          <span className="text-text-muted">{t('requisition.progress')}</span>
          <span className="tabular text-text">
            {stopped ? t('requisition.progressStopped') : `${passed} / ${total}`}
          </span>
        </div>
        <ol aria-label={t('requisition.progress')} className="flex items-center gap-1">
          {REQUISITION_MAIN_PATH.map((status, step) => {
            const done = step < passed;
            /* Chizmadagi bilan BIR XIL qoida: kutilayotgani — KEYINGI
               bo'g'in, bajarilgani emas. */
            const waiting = step === passed && !stopped;
            return (
              <li
                key={status}
                title={waiting ? requisitionPendingText(status) : requisitionStatusText(status)}
                className={cn(
                  'h-1.5 flex-1 rounded-full transition-colors duration-[var(--motion-base)]',
                  /* To'xtagan hujjatda chiziq O'RIN ko'rsatmaydi: qayerda
                     uzilgani ro'yxat javobida yo'q. U butunlay qizil
                     bo'lib, "yo'l shu yerda tugadi" deydi. */
                  stopped
                    ? 'bg-danger/30'
                    : done
                      ? 'bg-[var(--color-accent)]'
                      : 'bg-surface-2',
                  /* Kutilayotgani SARIQ va sekin nafas oladi — ilovada
                     sariq bitta narsani bildiradi: kutilmoqda. To'xtagan
                     hujjatda esa kutiladigan narsa yo'q. */
                  waiting
                    ? 'bg-warn motion-pulse-soft [--pulse-color:var(--color-warn)]'
                    : undefined,
                )}
              />
            );
          })}
        </ol>
      </div>

      <footer className="mt-auto flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5 text-xs">
        {/*
          MUDDAT — sana YOLG'IZ turganda u yaqinmi yoki uzoqmi ekani
          bilinmaydi: odam har safar kalendarni ochib sanashi kerak
          bo'lardi. Shuning uchun sana yonida qolgan kun ham yoziladi.
        */}
        <span
          className={cn(
            'flex items-center gap-1.5 leading-5',
            overdue ? 'text-danger' : urgent ? 'text-warn' : 'text-text-muted',
          )}
        >
          <CalendarClock size={13} aria-hidden className="shrink-0" />
          {needed === null ? (
            t('requisition.noDeadline')
          ) : (
            <>
              <span className="tabular">{needed}</span>
              {daysLeft === null || deadline === 'done' ? null : (
                <span className={cn('tabular', overdue || urgent ? '' : 'text-text-faint')}>
                  ·{' '}
                  {overdue
                    ? t('requisition.daysPast', { count: Math.abs(daysLeft) })
                    : urgent
                      ? t('requisition.dueToday')
                      : t('requisition.daysLeft', { count: daysLeft })}
                </span>
              )}
            </>
          )}
        </span>

        {/* SO'RALGAN kun — muddat shu kundan sanaladi. */}
        <span className="flex items-center gap-1.5 leading-5 text-text-faint">
          {t('requisition.requestedOn', { date: formatDate(requisition.createdAt) ?? '—' })}
        </span>
        {/* BUYURTMACHI — pastki o'ng burchakda. Bu yerda ilgari
            «Через кого» (`throughPerson` — mahsulotni OLIB KETADIGAN odam)
            turardi va uni buyurtmachi deb o'qishardi; u hujjat ichida qoladi.
            Ism topilmasa blok umuman chizilmaydi.
            Tor ekranda butunlay keyingi qatorga o'tadi (`flex-wrap`),
            sig'masa faqat ism qisqaradi — yorliq va ikonka joyida qoladi. */}
        {creator === null ? null : (
          <span
            title={`${t('requisition.createdBy')} ${creator}`}
            className="ml-auto flex max-w-full min-w-0 items-center gap-1.5 text-xs leading-5"
          >
            <User size={14} aria-hidden className="shrink-0 text-text-muted" />
            <span className="shrink-0 text-text-muted">{t('requisition.createdBy')}</span>
            <span className="min-w-0 truncate font-medium text-text">{creator}</span>
          </span>
        )}
      </footer>
    </Card>
  );
};
